/* Post pricing-simplification (2026-06) — /pricing page.
 *
 * V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Two active tiers only: Free + Premium. Premium is billed at $14.99/mo or
 * $149/yr (17% off vs $179.88 monthly-equivalent). Free absorbs the legacy
 * Member feature set, so no separate paid mid-tier appears here.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, CheckCircle2, ArrowRight, Clock4, Tag, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import ThemeToggle from "@/components/v2/ThemeToggle";
import { APP_NAME } from "@/theme";

function dollars(cents) {
  const v = Number(cents || 0) / 100;
  return v % 1 === 0 ? `$${v.toFixed(0)}` : `$${v.toFixed(2)}`;
}

function annualPriceCents(plan) {
  if (plan.price_annual_cents != null) return Number(plan.price_annual_cents);
  const discount = Number(plan.annual_discount_percent ?? 20);
  const monthly = Number(plan.price_monthly_cents || 0);
  return Math.round(monthly * 12 * (1 - discount / 100));
}

function annualPerMonthCents(plan) {
  return Math.round(annualPriceCents(plan) / 12);
}

export default function Pricing() {
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState("monthly");
  const [busyCode, setBusyCode] = useState(null);

  const initialCode = (searchParams.get("code") || "").toUpperCase();
  const [promoInput, setPromoInput] = useState(initialCode);
  const [promoValidating, setPromoValidating] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        const items = (data?.items || []).slice().sort((a, b) => {
          const oa = a.display_order ?? a.sort_priority ?? 0;
          const ob = b.display_order ?? b.sort_priority ?? 0;
          if (oa !== ob) return oa - ob;
          return (a.name || "").localeCompare(b.name || "");
        });
        setPlans(items);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function startCheckout(plan) {
    if (plan.code === "free" || plan.is_paid === false) {
      navigate(authenticated ? "/dashboard" : "/signup?next=/dashboard");
      return;
    }
    if (!authenticated) {
      navigate(`/signup?next=/pricing&plan=${plan.id}&billing_cycle=${cycle}`);
      return;
    }
    const priceId = cycle === "annual" ? plan.stripe_price_id_annual : plan.stripe_price_id_monthly;
    if (!priceId) {
      toast.error(
        `${plan.name} (${cycle}) isn't wired to Stripe yet. An admin must paste the Stripe Price ID under /admin/plans.`
      );
      return;
    }
    setBusyCode(plan.code);
    try {
      const { data } = await api.post("/payments/checkout-session", {
        plan_id: plan.id,
        billing_cycle: cycle,
        origin_url: window.location.origin,
        coupon_code: appliedPromo?.code || undefined,
      });
      window.location.href = data.url;
    } catch (e) {
      if (e?.response?.status === 409) {
        const detail = e?.response?.data?.detail || {};
        toast.success(
          detail.message || "You already have an active subscription — opening your billing center.",
        );
        setBusyCode(null);
        navigate("/account/billing");
        return;
      }
      const detail = e?.response?.data?.detail;
      const structured = detail && typeof detail === "object" && detail.error
        ? detail
        : null;
      if (structured) {
        console.warn("[pricing] Checkout failed →", structured.error, structured.detail_for_admin);
        toast.error(structured.message || "Checkout could not start. Please try again.");
      } else {
        toast.error(formatApiError(e));
      }
      setBusyCode(null);
    }
  }

  const paidPlans = plans.filter(p => p.is_paid);
  const maxDiscount = paidPlans.length
    ? Math.max(...paidPlans.map(p => Number(p.annual_discount_percent ?? 20)))
    : 17;

  const primaryPaidPlan = useMemo(
    () => paidPlans.find((p) => p.code === "premium") || paidPlans[0] || null,
    [paidPlans],
  );

  async function applyPromo(e) {
    if (e) e.preventDefault();
    const code = (promoInput || "").trim().toUpperCase();
    if (!code) return;
    setPromoValidating(true);
    setPromoError(null);
    try {
      const { data } = await api.post("/coupons/validate", {
        code,
        plan_id: primaryPaidPlan?.id,
      });
      if (data.valid) {
        setAppliedPromo({ code, coupon: data.coupon, computed_discount: data.computed_discount });
        setPromoError(null);
        toast.success(`Promo code ${code} applied.`);
      } else {
        setAppliedPromo(null);
        setPromoError(data.message || data.error || "This promo code isn't valid.");
      }
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(formatApiError(err));
    } finally {
      setPromoValidating(false);
    }
  }

  function clearPromo() {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError(null);
  }

  useEffect(() => {
    if (initialCode && primaryPaidPlan && !appliedPromo && !promoValidating) {
      applyPromo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode, primaryPaidPlan?.id]);

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-v2-background text-v2-text">
      <header className="border-b border-v2-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="pricing-back-home"
            className="inline-flex items-baseline gap-1 text-v2-heading-sm font-semibold text-v2-text hover:opacity-90 transition-opacity"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            <span>{APP_NAME}</span>
            <span
              aria-hidden="true"
              className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-v2-accent"
            />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="text-v2-caption uppercase tracking-[0.18em] text-v2-accentStrong mb-3">
            Pricing
          </div>
          <h1
            className="text-v2-display-md md:text-v2-display-lg text-v2-text mb-4 tracking-tight"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Simple pricing. One paid tier.
          </h1>
          <p className="text-v2-body-lg text-v2-textMuted max-w-xl mx-auto leading-relaxed">
            Start free with the full readiness and credit-analysis experience. Upgrade to
            Premium when you're ready to draft dispute letters and unlock the full funding
            playbook.
          </p>
        </div>

        {/* Monthly / Annual toggle */}
        <div data-testid="pricing-billing-toggle" className="flex justify-center mb-10">
          <div className="inline-flex rounded-full p-1 border border-v2-border bg-v2-surface">
            <button
              data-testid="pricing-toggle-monthly"
              onClick={() => setCycle("monthly")}
              className={`px-4 py-1.5 text-v2-body-sm rounded-full font-medium transition-all ${
                cycle === "monthly"
                  ? "bg-v2-accentStrong text-v2-accentInk shadow-v2-subtle"
                  : "bg-transparent text-v2-textMuted hover:text-v2-text"
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="pricing-toggle-annual"
              onClick={() => setCycle("annual")}
              className={`px-4 py-1.5 text-v2-body-sm rounded-full font-medium transition-all inline-flex items-center gap-1.5 ${
                cycle === "annual"
                  ? "bg-v2-accentStrong text-v2-accentInk shadow-v2-subtle"
                  : "bg-transparent text-v2-textMuted hover:text-v2-text"
              }`}
            >
              Annual
              <span
                data-testid="pricing-toggle-annual-savings"
                className={`text-v2-caption uppercase rounded-full px-1.5 py-0.5 ${
                  cycle === "annual"
                    ? "bg-v2-accentInk/20 text-v2-accentInk"
                    : "bg-v2-successSoft text-v2-success"
                }`}
              >
                Save {maxDiscount}%
              </span>
            </button>
          </div>
        </div>

        {/* Promo code input */}
        <div className="flex justify-center mb-8 px-2" data-testid="pricing-promo-block">
          <form
            onSubmit={applyPromo}
            className={`w-full max-w-md rounded-v2-lg border p-3 transition-colors ${
              appliedPromo
                ? "bg-v2-successSoft border-v2-success/40"
                : "bg-v2-surface border-v2-border"
            }`}
          >
            {appliedPromo ? (
              <div className="flex items-center gap-3" data-testid="pricing-promo-applied">
                <CheckCircle2 className="w-4 h-4 text-v2-success shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-v2-caption uppercase tracking-[0.14em] text-v2-success">
                    Promo applied — <span className="font-mono">{appliedPromo.code}</span>
                  </div>
                  <div className="text-v2-body-sm text-v2-textMuted leading-snug">
                    {appliedPromo.computed_discount?.pretty_discount || "Discount"}{" "}
                    {appliedPromo.computed_discount?.duration_label || ""}
                    {appliedPromo.computed_discount?.discounted_price_cents !== undefined && (
                      <>
                        {" · "}
                        <span className="text-v2-text">
                          {`Effective ${dollars(appliedPromo.computed_discount.discounted_price_cents)}/mo`}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="pricing-promo-clear"
                  onClick={clearPromo}
                  className="text-v2-textMuted hover:text-v2-text transition-colors"
                  aria-label="Remove promo code"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-v2-accent shrink-0 ml-1" />
                <input
                  data-testid="pricing-promo-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Have a promo code?"
                  className="flex-1 bg-transparent px-1 py-1 text-v2-body-sm font-mono text-v2-text focus:outline-none placeholder:text-v2-textSubtle"
                  spellCheck={false}
                  autoComplete="off"
                />
                <button
                  type="submit"
                  data-testid="pricing-promo-apply"
                  disabled={promoValidating || !promoInput.trim()}
                  className="inline-flex items-center gap-1.5 rounded-v2-md px-3 py-1.5 text-v2-body-sm font-semibold bg-v2-accent text-v2-accentInk hover:bg-v2-accentStrong transition-colors disabled:opacity-50"
                >
                  {promoValidating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Apply
                </button>
              </div>
            )}
            {promoError && (
              <div
                data-testid="pricing-promo-error"
                className="mt-2 pl-6 text-v2-caption text-v2-danger"
              >
                {promoError}
              </div>
            )}
            <div className="mt-2 pl-6 text-v2-caption text-v2-textSubtle leading-relaxed">
              Coupons apply as configured. Charges reflect the discounted amount and are
              processed by Stripe. Cancel anytime through your account.
            </div>
          </form>
        </div>

        {loading ? (
          <div
            data-testid="pricing-loading"
            className="text-center text-v2-body-sm text-v2-textMuted"
          >
            Loading plans…
          </div>
        ) : (
          <div
            data-testid="pricing-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto"
          >
            {plans.map(p => {
              const featured = !!p.is_paid;
              const isFree = !p.is_paid;
              const monthlyCents = Number(p.price_monthly_cents || 0);
              const baseDisplayCents = cycle === "annual" ? annualPerMonthCents(p) : monthlyCents;
              const totalAnnualCents = annualPriceCents(p);
              const trialDays = Number(p.trial_days || 0);
              const promoAppliesToPlan =
                appliedPromo?.coupon &&
                (!appliedPromo.coupon.applies_to_plans?.length ||
                  appliedPromo.coupon.applies_to_plans.includes(p.code));
              let discountedDisplayCents = null;
              if (!isFree && promoAppliesToPlan) {
                const cVal = Number(appliedPromo.coupon.discount_value || 0);
                if (appliedPromo.coupon.discount_type === "percentage") {
                  discountedDisplayCents = Math.max(
                    0,
                    Math.round(baseDisplayCents * (1 - cVal / 100)),
                  );
                } else {
                  discountedDisplayCents = Math.max(0, baseDisplayCents - Math.round(cVal));
                }
              }
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.code}`}
                  className={`rounded-v2-2xl p-7 relative flex flex-col transition-shadow ${
                    featured
                      ? "bg-v2-surface border border-v2-accent/40 shadow-v2-lifted"
                      : "bg-v2-surface border border-v2-border shadow-v2-card"
                  }`}
                >
                  {featured && (
                    <div
                      data-testid={`pricing-featured-badge-${p.code}`}
                      className="absolute -top-3 left-7 px-2.5 py-0.5 text-v2-caption uppercase tracking-[0.16em] rounded-full bg-v2-accent text-v2-accentInk"
                    >
                      Most popular
                    </div>
                  )}
                  {trialDays > 0 && !isFree && (
                    <div
                      data-testid={`pricing-trial-badge-${p.code}`}
                      className="absolute top-7 right-5 inline-flex items-center gap-1 text-v2-caption uppercase tracking-[0.12em] rounded-full px-2 py-0.5 bg-v2-successSoft text-v2-success"
                    >
                      <Clock4 className="w-2.5 h-2.5" /> {trialDays}-day free trial
                    </div>
                  )}
                  <div className="text-v2-caption uppercase tracking-[0.18em] text-v2-accent mb-2">
                    {p.code}
                  </div>
                  <h3
                    className="text-v2-heading-lg text-v2-text mb-1"
                    style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                  >
                    {p.name}
                  </h3>
                  {discountedDisplayCents !== null ? (
                    <div
                      className="flex items-baseline gap-2 mb-1 flex-wrap"
                      data-testid={`pricing-price-block-${p.code}`}
                    >
                      <span
                        data-testid={`pricing-price-${p.code}`}
                        className="text-v2-display-md text-v2-success"
                        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                      >
                        {`$${(discountedDisplayCents / 100).toFixed(2)}`}
                      </span>
                      <span
                        data-testid={`pricing-price-original-${p.code}`}
                        className="text-v2-heading-md line-through text-v2-textSubtle"
                      >
                        {`$${(baseDisplayCents / 100).toFixed(2)}`}
                      </span>
                      <span className="text-v2-textSubtle text-v2-body-sm">/month</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1 mb-1">
                      <span
                        data-testid={`pricing-price-${p.code}`}
                        className="text-v2-display-md text-v2-text"
                        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
                      >
                        {isFree ? "$0" : `$${(baseDisplayCents / 100).toFixed(2)}`}
                      </span>
                      <span className="text-v2-textSubtle text-v2-body-sm">
                        {isFree ? "forever" : "/month"}
                      </span>
                    </div>
                  )}
                  {discountedDisplayCents !== null && (
                    <div
                      data-testid={`pricing-promo-label-${p.code}`}
                      className="text-v2-caption uppercase tracking-[0.14em] mb-2 text-v2-success"
                    >
                      with <span className="font-mono">{appliedPromo.code}</span> applied
                      {appliedPromo.coupon.duration === "forever"
                        ? " · lifetime"
                        : appliedPromo.coupon.duration === "repeating"
                        ? ` · first ${appliedPromo.coupon.duration_in_months || 1} months`
                        : " · first invoice"}
                    </div>
                  )}
                  {cycle === "annual" && !isFree && (
                    <div
                      data-testid={`pricing-annual-total-${p.code}`}
                      className="text-v2-body-sm text-v2-textMuted mb-3"
                    >
                      Billed{" "}
                      <span className="text-v2-text">{dollars(totalAnnualCents)}/year</span>
                      {" — "}
                      <span className="text-v2-success">
                        save {p.annual_discount_percent || 17}% vs monthly
                      </span>
                    </div>
                  )}
                  <p className="text-v2-body-md text-v2-textMuted mb-5 leading-relaxed">
                    {p.description}
                  </p>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {(p.features || []).map(f => (
                      <li
                        key={f}
                        data-testid={`pricing-feature-${p.code}-${f}`}
                        className="flex items-start gap-2 text-v2-body-md text-v2-textMuted"
                      >
                        <Check className="w-4 h-4 flex-shrink-0 mt-1 text-v2-success" />
                        <span>{f.replace(/_/g, " ")}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    data-testid={`pricing-cta-${p.code}`}
                    disabled={busyCode === p.code}
                    onClick={() => startCheckout(p)}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-v2-md px-4 py-2.5 text-v2-body-sm font-semibold disabled:opacity-60 transition-all ${
                      featured
                        ? "bg-v2-accent text-v2-accentInk hover:bg-v2-accentStrong shadow-v2-card hover:shadow-v2-lifted"
                        : "bg-transparent text-v2-text border border-v2-border hover:bg-v2-surfaceSubtle"
                    }`}
                  >
                    {busyCode === p.code
                      ? "Redirecting…"
                      : isFree
                      ? "Get started"
                      : trialDays > 0
                      ? `Start ${trialDays}-day trial`
                      : "Subscribe"}
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Compliance footer — mandatory copy */}
        <p
          data-testid="pricing-compliance-disclaimer"
          className="text-center text-v2-body-sm text-v2-textSubtle mt-10 leading-relaxed max-w-2xl mx-auto"
        >
          All charges are billed by Informed OS via Stripe. Cancel anytime through
          your account. Subscription does not guarantee funding approval or credit improvement.
        </p>
      </main>
    </div>
  );
}
